import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
  User,
} from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';
import { userService } from '../services/userService';
import { accessService } from '../services/accessService';
import { AccessStatus, AppUser } from '../types';

/**
 * Maintenance mode lets only admins in. Turn it on with
 * REACT_APP_MAINTENANCE_MODE=true in .env and rebuild.
 */
const MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

interface AuthContextType {
  user: User | null;
  appUser: AppUser | null;
  status: AccessStatus;
  isAdmin: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  /** Creates a password account for an invited address. Fails with `auth/not-invited` otherwise. */
  activateInvitation: (email: string, password: string, displayName: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  resendVerification: () => Promise<void>;
  /** Re-checks verification and invitation (e.g. after clicking the e-mail link). */
  recheckAccess: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

const authError = (code: string) => Object.assign(new Error(code), { code });

const usesPasswordOnly = (user: User) =>
  user.providerData.length > 0 && user.providerData.every((p) => p.providerId === 'password');

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [status, setStatus] = useState<AccessStatus>('loading');

  const evaluate = useCallback(async (current: User | null) => {
    setUser(current);
    if (!current) {
      setAppUser(null);
      setStatus('signedOut');
      return;
    }

    try {
      const existing = await userService.getUser(current.uid);
      const isAdmin = existing?.isAdmin === true;

      if (MAINTENANCE_MODE && !isAdmin) {
        setAppUser(existing);
        setStatus('maintenance');
        return;
      }

      if (!isAdmin) {
        if (!current.emailVerified && usesPasswordOnly(current)) {
          setAppUser(existing);
          setStatus('unverified');
          return;
        }
        const invited = current.email ? await accessService.isInvited(current.email) : false;
        if (!invited) {
          setAppUser(existing);
          setStatus('denied');
          return;
        }
      }

      const profile = await userService.upsertProfile(current, existing).catch((error) => {
        console.error('Could not save user profile:', error);
        return existing;
      });
      setAppUser(profile ? { ...profile, isAdmin } : null);
      setStatus('ready');
    } catch (error) {
      console.error('Access check failed:', error);
      setAppUser(null);
      setStatus('denied');
    }
  }, []);

  useEffect(() => onAuthStateChanged(auth, (current) => void evaluate(current)), [evaluate]);

  const signInWithGoogle = async () => {
    await signInWithPopup(auth, googleProvider);
  };

  const signInWithEmail = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email.trim(), password);
  };

  const activateInvitation = async (email: string, password: string, displayName: string) => {
    const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
    const invited = await accessService.isInvited(email).catch(() => false);
    if (!invited) {
      // Do not leave accounts behind for addresses nobody invited.
      await credential.user.delete().catch(() => firebaseSignOut(auth));
      throw authError('auth/not-invited');
    }
    await updateProfile(credential.user, { displayName: displayName.trim() });
    await sendEmailVerification(credential.user, { url: window.location.origin });
    await evaluate(auth.currentUser);
  };

  const sendPasswordReset = async (email: string) => {
    await sendPasswordResetEmail(auth, email.trim(), { url: `${window.location.origin}/login` });
  };

  const resendVerification = async () => {
    if (auth.currentUser) await sendEmailVerification(auth.currentUser, { url: window.location.origin });
  };

  const recheckAccess = async () => {
    const current = auth.currentUser;
    if (!current) return;
    await current.reload();
    // The verified flag reaches the security rules only through a fresh ID token.
    await current.getIdToken(true);
    await evaluate(auth.currentUser);
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  const value: AuthContextType = {
    user,
    appUser,
    status,
    isAdmin: appUser?.isAdmin === true,
    signInWithGoogle,
    signInWithEmail,
    activateInvitation,
    sendPasswordReset,
    resendVerification,
    recheckAccess,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
